import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import moment from "moment";
import ButtonWithSpinner from "../ButtonWithSpinner";
import useAppointments from "../../hooks/useAppointments";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "confirmed", label: "Confirmada" },
  { value: "completed", label: "Completada" },
  { value: "cancelled", label: "Cancelada" },
];

const EMPTY_APPOINTMENT = {
  title: "",
  startAt: "",
  endAt: "",
  status: "pending",
  contactId: "",
  userId: "",
};

const toLocalInput = (value) => (value ? moment(value).format("YYYY-MM-DDTHH:mm") : "");

const AppointmentModal = ({ open, onClose, appointment, initialStart, initialContact, users, currentUser, canAssignOthers, onSaved }) => {
  const { save, update } = useAppointments();
  const [record, setRecord] = useState(EMPTY_APPOINTMENT);
  const [contact, setContact] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appointment) {
      setRecord({
        title: appointment.title || "",
        startAt: toLocalInput(appointment.startAt),
        endAt: toLocalInput(appointment.endAt),
        status: appointment.status || "pending",
        contactId: appointment.contactId || "",
        userId: appointment.userId || "",
      });
      setContact(appointment.contact || null);
    } else {
      const start = initialStart ? moment(initialStart).hour(9).minute(0) : null;
      setRecord({
        ...EMPTY_APPOINTMENT,
        userId: canAssignOthers ? "" : currentUser?.id || "",
        startAt: start ? start.format("YYYY-MM-DDTHH:mm") : "",
        endAt: start ? start.clone().add(30, "minutes").format("YYYY-MM-DDTHH:mm") : "",
      });
      setContact(initialContact || null);
    }
  }, [appointment, open, canAssignOthers, currentUser, initialStart, initialContact]);

  useEffect(() => {
    if (!open) return;
    const delay = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: contactSearch, pageNumber: 1 },
        });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [contactSearch, open]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    if (!record.title.trim() || !record.startAt || !record.endAt || !contact) return;

    setSaving(true);
    try {
      const payload = { ...record, contactId: contact.id, userId: record.userId || null };
      if (appointment?.id) {
        await update({ id: appointment.id, ...payload });
      } else {
        await save(payload);
      }
      toast.success("Cita guardada.");
      onSaved();
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{appointment?.id ? "Editar cita" : "Nueva cita"}</DialogTitle>
      <DialogContent dividers>
        <Grid spacing={2} container>
          <Grid size={12}>
            <TextField
              label="Título"
              size="small"
              fullWidth
              value={record.title}
              onChange={(e) => setRecord((prev) => ({ ...prev, title: e.target.value }))}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              options={contactOptions}
              getOptionLabel={(option) => option.name || option.number || ""}
              value={contact}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setContact(value)}
              onInputChange={(_, value) => setContactSearch(value)}
              renderInput={(params) => (
                <TextField {...params} label="Contacto" size="small" placeholder="Buscar por nombre o número" />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Inicio"
              type="datetime-local"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              value={record.startAt}
              onChange={(e) => setRecord((prev) => ({ ...prev, startAt: e.target.value }))}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Fin"
              type="datetime-local"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              value={record.endAt}
              onChange={(e) => setRecord((prev) => ({ ...prev, endAt: e.target.value }))}
            />
          </Grid>
          {canAssignOthers && (
            <Grid size={{ xs: 6 }}>
              <TextField
                select
                label="Agente"
                size="small"
                fullWidth
                value={record.userId}
                onChange={(e) => setRecord((prev) => ({ ...prev, userId: e.target.value }))}
              >
                <MenuItem value="">Sin asignar</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid size={{ xs: 6 }}>
            <TextField
              select
              label="Estado"
              size="small"
              fullWidth
              value={record.status}
              onChange={(e) => setRecord((prev) => ({ ...prev, status: e.target.value }))}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonWithSpinner onClick={handleClose} variant="outlined" loading={saving}>
          Cancelar
        </ButtonWithSpinner>
        <ButtonWithSpinner onClick={handleSubmit} variant="contained" color="primary" loading={saving}>
          Guardar
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentModal;
